// src/pages/call-center/components/EditSlabsModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Check, Loader, Plus, Trash2 } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { useBusinessSettings } from '../../../hooks/useBusinessSettings';
import FieldError from '../../message-center/components/FieldError';
import { validateRanges, blockInvalidNumberKeys, blockNonIntegerKeys, sanitizeNumber } from '../../message-center/utils/rangeValidation';
import styles from './EditSlabsModal.module.css';

const EMPTY_SLAB = { minValue: '', maxValue: '', rate: '' };

const EditSlabsModal = ({ onClose, onSuccess }) => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const crossSelling = useBusinessSettings('crossSelling');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [slabs, setSlabs] = useState([{ ...EMPTY_SLAB }]);

  useEffect(() => {
    const existing = crossSelling.settings.slabs;
    if (Array.isArray(existing) && existing.length > 0) {
      setSlabs(existing.map((s, i) => ({
        minValue: s.minValue != null ? String(s.minValue) : '',
        maxValue: s.maxValue != null && i < existing.length - 1 ? String(s.maxValue) : '',
        rate: s.rate != null ? String(s.rate) : '',
      })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crossSelling.isLoading]);

  const handleAddSlab = () => {
    setSlabs((prev) => {
      const last = prev[prev.length - 1];
      const nextMin = last && /^\d+$/.test(last.maxValue) ? String(parseInt(last.maxValue, 10) + 1) : '';
      return [...prev, { ...EMPTY_SLAB, minValue: nextMin }];
    });
    setErrors({});
  };

  const handleSlabChange = (index, field, value) => {
    const clean = sanitizeNumber(value, field === 'rate');
    setSlabs((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: clean };
      return next;
    });
    const errorKey = `slabs.${index}.${field}`;
    const rowKey = `slabs.${index}.row`;
    if (errors[errorKey] || errors[rowKey]) {
      setErrors((prev) => ({ ...prev, [errorKey]: '', [rowKey]: '' }));
    }
  };

  // The last slab is always open-ended, so the new last row loses its max value.
  const handleRemoveSlab = (index) => {
    setSlabs((prev) => {
      const next = prev.filter((_, i) => i !== index);
      next[next.length - 1] = { ...next[next.length - 1], maxValue: '' };
      return next;
    });
    setErrors({});
  };

  const validate = () => {
    const newErrors = validateRanges(slabs, { valueField: 'rate', prefix: 'slabs', t, maxValue: 100 });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      showError(t('callCenterShifts.validation.fixErrors'));
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = slabs.map((s, i) => ({
        minValue: parseInt(s.minValue, 10),
        maxValue: i === slabs.length - 1 ? null : parseInt(s.maxValue, 10),
        rate: parseFloat(s.rate),
      }));

      await crossSelling.saveSettings({ ...crossSelling.settings, slabs: payload });
      success(t('callCenterShifts.slabsSavedSuccess'));
      onSuccess();
    } catch (err) {
      console.error('Error saving commission slabs:', err);
      showError(err.response?.data?.message || t('callCenterShifts.failedToSaveSlabs'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const minPlaceholder = (index) => {
    if (index === 0) return t('messageCenterShiftTypes.placeholders.firstMin');
    const prevMax = slabs[index - 1]?.maxValue;
    return /^\d+$/.test(prevMax)
      ? t('messageCenterShiftTypes.placeholders.nextMin', { value: parseInt(prevMax, 10) + 1 })
      : '';
  };

  return (
    <div className={styles.modalOverlay} onClick={(e) => { if (e.target === e.currentTarget && !isSubmitting) onClose(); }}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>{t('callCenterShifts.editSlabs')}</h2>
          <button onClick={onClose} className={styles.closeButton} disabled={isSubmitting}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.modalBody}>
          <p className={styles.helpText}>{t('callCenterShifts.slabsHelp')}</p>

          <div className={styles.slabsHeader}>
            <label>{t('callCenterShifts.commissionSlabs')}</label>
            <button type="button" onClick={handleAddSlab} className={styles.addSlabButton} disabled={isSubmitting}>
              <Plus size={16} />
              {t('callCenterShifts.addSlab')}
            </button>
          </div>

          <div className={styles.slabsList}>
            <div className={styles.slabsListHeader}>
              <span>{t('callCenterShifts.slabMinValue')}</span>
              <span>{t('callCenterShifts.slabMaxValue')}</span>
              <span>{t('callCenterShifts.slabRate')}</span>
              <span />
            </div>
            {slabs.map((slab, index) => {
              const isLast = index === slabs.length - 1;
              const err = (field) => errors[`slabs.${index}.${field}`];
              return (
                <div className={`${styles.slabRow} ${err('row') ? styles.rowError : ''}`} key={index}>
                  <div className={styles.fieldCell}>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      placeholder={minPlaceholder(index)}
                      value={slab.minValue}
                      onKeyDown={blockNonIntegerKeys}
                      onChange={(e) => handleSlabChange(index, 'minValue', e.target.value)}
                      className={err('minValue') ? styles.error : ''}
                      disabled={isSubmitting}
                    />
                    <FieldError message={err('minValue')} floating />
                  </div>
                  <div className={styles.fieldCell}>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      placeholder={isLast ? t('callCenterShifts.openEnded') : t('messageCenterShiftTypes.placeholders.maxValue')}
                      value={isLast ? '' : slab.maxValue}
                      onKeyDown={blockNonIntegerKeys}
                      onChange={(e) => handleSlabChange(index, 'maxValue', e.target.value)}
                      className={err('maxValue') ? styles.error : ''}
                      disabled={isSubmitting || isLast}
                    />
                    <FieldError message={err('maxValue')} floating />
                  </div>
                  <div className={styles.fieldCell}>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder={t('messageCenterShiftTypes.placeholders.rate')}
                      value={slab.rate}
                      onKeyDown={blockInvalidNumberKeys}
                      onChange={(e) => handleSlabChange(index, 'rate', e.target.value)}
                      className={err('rate') ? styles.error : ''}
                      disabled={isSubmitting}
                    />
                    <FieldError message={err('rate')} floating />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveSlab(index)}
                    className={styles.removeSlabButton}
                    disabled={isSubmitting || slabs.length === 1}
                    title={t('callCenterShifts.removeSlab')}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              );
            })}
          </div>
        </form>

        <div className={styles.modalFooter}>
          <button type="button" onClick={onClose} className={styles.cancelButton} disabled={isSubmitting}>
            {t('common.cancel')}
          </button>
          <button type="submit" onClick={handleSubmit} className={styles.submitButton} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader className={styles.buttonSpinner} size={18} />
                {t('common.saving')}
              </>
            ) : (
              <>
                <Check size={18} />
                {t('callCenterShifts.saveSlabs')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditSlabsModal;
